import { useState, type ReactNode } from 'react'
import { toast } from 'sonner'
import {
  AlertDialog,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogTitle,
} from '@/components/ui/alert-dialog'
import { Button } from '@/components/ui/button'
import { useTripSetState } from '@/lib/hooks'

interface TripLegLike {
  crew?: Array<unknown> | null
  passengers?: Array<unknown> | null
  arrivalDateTime: string | Date
  departureAirportIcao?: string
  arrivalAirportIcao?: string
  tasks?: Array<{ status: string }> | null
}

interface UseTripStatusActionsParams {
  tripId: string
  legs?: TripLegLike[] | null
}

interface UseTripStatusActionsResult {
  /** Validate legs and either schedule or open the no-crew confirmation. */
  requestSchedule: () => void
  /** Validate flights/tasks and open the mark-complete confirmation. */
  requestComplete: () => void
  isPending: boolean
  /** Confirmation dialogs — render once per consumer. */
  dialogs: ReactNode
}

/**
 * Shared schedule/complete transition logic (validation + confirmation dialogs)
 * used by the trip card's status pill dropdown (TripStatusSelect). Keeps a single
 * source of truth for the transition guardrails.
 */
export function useTripStatusActions({
  tripId,
  legs,
}: UseTripStatusActionsParams): UseTripStatusActionsResult {
  const [showCompleteDialog, setShowCompleteDialog] = useState(false)
  const [showScheduleNoCrewDialog, setShowScheduleNoCrewDialog] = useState(false)

  const { mutate: setTripState, isPending } = useTripSetState()

  const scheduleTrip = () => {
    setTripState(
      { id: tripId, tripState: 'Scheduled' },
      {
        onSuccess: () => toast.success('Trip scheduled'),
        onError: (err) => toast.error(err.message || 'Failed to schedule trip'),
      },
    )
  }

  const requestSchedule = () => {
    const tripLegs = legs || []
    if (tripLegs.length === 0) {
      toast.error('Add at least one flight before scheduling')
      return
    }
    // Flights without crew don't block scheduling — confirm with the user, then
    // schedule anyway (crew can be assigned later).
    const hasFlightMissingCrew = tripLegs.some((leg) => !leg.crew || leg.crew.length === 0)
    if (hasFlightMissingCrew) {
      setShowScheduleNoCrewDialog(true)
      return
    }
    scheduleTrip()
  }

  const handleConfirmScheduleNoCrew = () => {
    setShowScheduleNoCrewDialog(false)
    scheduleTrip()
  }

  const requestComplete = () => {
    const tripLegs = legs || []
    const now = new Date()
    const futureFlights = tripLegs.filter((leg) => new Date(leg.arrivalDateTime) > now)
    if (futureFlights.length > 0) {
      toast.error(`Cannot complete trip: ${futureFlights.length} flight(s) are still in the future`)
      return
    }
    // Only Draft tasks block completion — Read means submitted-then-reviewed.
    const legsWithDraftTasks = tripLegs.filter((leg) =>
      (leg.tasks || []).some((task) => task.status === 'Draft'),
    )
    const draftTaskCount = legsWithDraftTasks.reduce(
      (n, leg) => n + (leg.tasks || []).filter((task) => task.status === 'Draft').length,
      0,
    )
    if (draftTaskCount > 0) {
      const routes = [
        ...new Set(
          legsWithDraftTasks
            .filter((leg) => leg.departureAirportIcao && leg.arrivalAirportIcao)
            .map((leg) => `${leg.departureAirportIcao} → ${leg.arrivalAirportIcao}`),
        ),
      ].join(', ')
      toast.error(
        `Cannot complete trip: ${draftTaskCount} task(s) have not been submitted${routes ? ` (${routes})` : ''}`,
      )
      return
    }
    setShowCompleteDialog(true)
  }

  const handleConfirmComplete = () => {
    setTripState(
      { id: tripId, tripState: 'Complete' },
      {
        onSuccess: () => {
          setShowCompleteDialog(false)
          toast.success('Trip marked as complete')
        },
        onError: (err) => toast.error(err.message || 'Failed to mark trip as complete'),
      },
    )
  }

  const dialogs = (
    <>
      {/* Mark as Complete Confirmation Dialog */}
      <AlertDialog open={showCompleteDialog} onOpenChange={setShowCompleteDialog}>
        <AlertDialogContent className="p-8 gap-0 text-center">
          <AlertDialogTitle className="text-xl font-semibold text-center leading-[1.2] tracking-[-0.4px]">
            Mark as Complete
          </AlertDialogTitle>
          <AlertDialogDescription className="text-sm text-content-primary-subtle text-center mt-4">
            Are you sure you want to mark this trip as complete?
          </AlertDialogDescription>
          <div className="flex flex-col gap-2 mt-4 w-full">
            <Button
              variant="primary"
              size="mini"
              className="w-full"
              onClick={handleConfirmComplete}
              disabled={isPending}
            >
              {isPending ? 'Completing...' : 'Complete'}
            </Button>
            <Button
              variant="secondary"
              size="mini"
              className="w-full"
              onClick={() => setShowCompleteDialog(false)}
              disabled={isPending}
            >
              Cancel
            </Button>
          </div>
        </AlertDialogContent>
      </AlertDialog>

      {/* Schedule without Crew Confirmation Dialog */}
      <AlertDialog open={showScheduleNoCrewDialog} onOpenChange={setShowScheduleNoCrewDialog}>
        <AlertDialogContent className="p-8 gap-0 text-center">
          <AlertDialogTitle className="text-xl font-semibold text-center leading-[1.2] tracking-[-0.4px]">
            Schedule without Crew?
          </AlertDialogTitle>
          <AlertDialogDescription className="text-sm text-content-primary-subtle text-center mt-4">
            Some flights have no crew assigned. You can assign crew later. Schedule this trip anyway?
          </AlertDialogDescription>
          <div className="flex flex-col gap-2 mt-4 w-full">
            <Button
              variant="primary"
              size="mini"
              className="w-full"
              onClick={handleConfirmScheduleNoCrew}
              disabled={isPending}
            >
              {isPending ? 'Scheduling...' : 'Schedule Anyway'}
            </Button>
            <Button
              variant="secondary"
              size="mini"
              className="w-full"
              onClick={() => setShowScheduleNoCrewDialog(false)}
              disabled={isPending}
            >
              Cancel
            </Button>
          </div>
        </AlertDialogContent>
      </AlertDialog>
    </>
  )

  return { requestSchedule, requestComplete, isPending, dialogs }
}
